import { useMemo, useState } from "react";
import { Filter } from "lucide-react";
import { TESTIMONIALS, type Testimonial, type TestimonialCategory } from "../data/testimonials";
import TestimonialCard from "./TestimonialCard";
import TestimonialVideoLightbox from "./TestimonialVideoLightbox";
import ScrollReveal from "./ScrollReveal";

const CATEGORY_LABELS: Record<TestimonialCategory | "all", string> = {
  all: "All Stories",
  customer: "Customer Stories",
  producer: "Producer Stories",
  driver: "Driver Partner Stories",
};

const CATEGORY_TABS: Array<TestimonialCategory | "all"> = ["all", "customer", "producer", "driver"];

/** ZP-36's unified gallery — ZP-37/38's "Producer/Driver Stories" are just two values of this same category filter. */
export default function TestimonialGallery() {
  const [category, setCategory] = useState<TestimonialCategory | "all">("all");
  const [city, setCity] = useState("all");
  const [theme, setTheme] = useState("all");
  const [activeVideo, setActiveVideo] = useState<Testimonial | null>(null);

  const cities = useMemo(() => Array.from(new Set(TESTIMONIALS.map((t) => t.city))).sort(), []);
  const themes = useMemo(() => Array.from(new Set(TESTIMONIALS.flatMap((t) => t.theme))).sort(), []);

  const filtered = useMemo(
    () =>
      TESTIMONIALS.filter((t) => {
        if (category !== "all" && t.category !== category) return false;
        if (city !== "all" && t.city !== city) return false;
        if (theme !== "all" && !t.theme.includes(theme)) return false;
        return true;
      }),
    [category, city, theme]
  );

  return (
    <div>
      <div className="mb-8 flex flex-wrap items-center gap-3">
        <div className="flex flex-wrap gap-2">
          {CATEGORY_TABS.map((c) => (
            <button
              key={c}
              type="button"
              onClick={() => setCategory(c)}
              aria-pressed={category === c}
              className={`rounded-full px-4 py-2 text-xs font-semibold transition-colors ${
                category === c
                  ? "bg-teal-400 text-ocean-950"
                  : "bg-white text-ocean-800 ring-1 ring-ocean-900/10 hover:bg-foam-100"
              }`}
            >
              {CATEGORY_LABELS[c]}
            </button>
          ))}
        </div>

        <div className="ml-auto flex flex-wrap items-center gap-2">
          <Filter className="h-3.5 w-3.5 text-ocean-800/40" />
          <select
            value={city}
            onChange={(e) => setCity(e.target.value)}
            aria-label="Filter by city"
            className="rounded-full border border-ocean-900/10 bg-white px-3 py-1.5 text-xs text-ocean-800"
          >
            <option value="all">All cities</option>
            {cities.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
          <select
            value={theme}
            onChange={(e) => setTheme(e.target.value)}
            aria-label="Filter by theme"
            className="rounded-full border border-ocean-900/10 bg-white px-3 py-1.5 text-xs text-ocean-800"
          >
            <option value="all">All themes</option>
            {themes.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
        </div>
      </div>

      {filtered.length === 0 ? (
        <p className="py-12 text-center text-sm text-ocean-800/50">No stories match these filters yet.</p>
      ) : (
        <>
          <div className="hidden gap-6 sm:grid sm:grid-cols-2 lg:grid-cols-3">
            {filtered.map((t, i) => (
              <ScrollReveal key={t.id} delay={i * 0.06}>
                <TestimonialCard testimonial={t} onPlayVideo={() => setActiveVideo(t)} />
              </ScrollReveal>
            ))}
          </div>

          <div className="flex snap-x snap-mandatory gap-4 overflow-x-auto pb-2 sm:hidden [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            {filtered.map((t) => (
              <div key={t.id} className="w-[85%] shrink-0 snap-start">
                <TestimonialCard testimonial={t} onPlayVideo={() => setActiveVideo(t)} />
              </div>
            ))}
          </div>
        </>
      )}

      <TestimonialVideoLightbox testimonial={activeVideo} onClose={() => setActiveVideo(null)} />
    </div>
  );
}
